import React from "react";
import { UseFormReturn } from "react-hook-form";
import { CreditCard, Building, Hash, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { PaymentMethodsValues } from "@/schemas/sellerProfileSchema";

interface PaymentMethodFormProps {
  form: UseFormReturn<PaymentMethodsValues>;
  onSubmit: (data: PaymentMethodsValues) => void;
  submitButtonText?: string;
  showOtherPaymentMethods?: boolean;
  isUpdating?: boolean;
}

export const PaymentMethodForm = ({ 
  form, 
  onSubmit, 
  submitButtonText = "Save Payment Information",
  showOtherPaymentMethods = true,
  isUpdating = false,
}: PaymentMethodFormProps) => {
  const paymentServices = [
    { name: 'PayPal', key: 'paypalConnected' as const },
    { name: 'Venmo', key: 'cashAppConnected' as const },
    { name: 'Cash App', key: 'cashAppConnected' as const },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Information</CardTitle>
        <CardDescription>
          Manage your payment methods for receiving payments.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <CreditCard className="h-5 w-5" />
                <h3 className="text-lg font-medium">Direct Deposit</h3>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="accountHolderName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          Account Holder Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                            <Input placeholder="John Doe" className="pl-10 h-11" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="bankName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          Bank Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                            <Input placeholder="Your Bank" className="pl-10 h-11" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="routingNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          Routing Number
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Hash className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                            <Input placeholder="XXXXXXXXX" className="pl-10 h-11" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          Account Number
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Hash className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                            <Input placeholder="XXXXXXXX" className="pl-10 h-11" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button type="submit" className="w-full md:w-auto" disabled={isUpdating}>
                  {isUpdating ? "Saving..." : submitButtonText}
                </Button>
              </div>
            </div>

            {showOtherPaymentMethods && (
              <>
                <Separator />

                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <CreditCard className="h-5 w-5" />
                    <h3 className="text-lg font-medium">Other Payment Methods</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Connect other payment services to receive payments.
                  </p>

                  <div className="space-y-4">
                    {paymentServices.map((service) => (
                      <div key={service.key} className="flex items-center justify-between p-4 border rounded-lg">
                        <span className="font-medium">{service.name}</span>
                        <Button variant="outline">
                          Connect
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}; 